/**
 * FARID HUB PDP — gallery, variant matching, stock, size guide, tabs, zoom.
 * Does not fake variants. Sibling colors are native product links.
 */
(function () {
  const money = (cents, format) => {
    try {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, format || window.theme?.moneyFormat);
      }
    } catch (e) {}
    const value = (Number(cents) || 0) / 100;
    return `Dhs. ${value.toFixed(2)}`;
  };

  const reduced = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function parseJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function initTabs(root) {
    const tabRoot = root.querySelector('[data-fh-tabs]') || document.querySelector('[data-fh-tabs]');
    if (!tabRoot || tabRoot.dataset.bound === '1') return;
    tabRoot.dataset.bound = '1';
    const tabs = Array.from(tabRoot.querySelectorAll('[data-fh-tab]'));
    const panels = Array.from(tabRoot.querySelectorAll('[data-fh-panel]'));
    const visibleTabs = () => tabs.filter((tab) => tab.offsetParent !== null);
    const activate = (id) => {
      tabs.forEach((tab) => {
        const on = tab.getAttribute('data-fh-tab') === id;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        if (tab.hasAttribute('aria-expanded')) {
          tab.setAttribute('aria-expanded', on ? 'true' : 'false');
        }
      });
      panels.forEach((panel) => {
        const on = panel.getAttribute('data-fh-panel') === id;
        panel.classList.toggle('is-active', on);
        panel.hidden = !on;
      });
    };
    tabs.forEach((tab) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        activate(tab.getAttribute('data-fh-tab'));
      });
      tab.addEventListener('keydown', (event) => {
        const list = visibleTabs();
        const i = list.indexOf(tab);
        if (i < 0) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          list[(i + 1) % list.length].focus();
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          list[(i - 1 + list.length) % list.length].focus();
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate(tab.getAttribute('data-fh-tab'));
        }
      });
    });
  }

  function initSizeGuide(root) {
    const dialog = root.querySelector('[data-fh-size-guide]');
    if (!dialog) return;
    const openers = document.querySelectorAll('[data-fh-size-open]');
    const close = () => {
      if (typeof dialog.close === 'function') dialog.close();
    };
    openers.forEach((btn) => {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => {
        if (typeof dialog.showModal === 'function') dialog.showModal();
      });
    });
    dialog.querySelector('[data-fh-size-close]')?.addEventListener('click', close);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });

    dialog.querySelectorAll('[data-fh-sg-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        const id = tab.getAttribute('data-fh-sg-tab');
        dialog.querySelectorAll('[data-fh-sg-tab]').forEach((t) => {
          const on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        dialog.querySelectorAll('[data-fh-sg-panel]').forEach((panel) => {
          const on = panel.getAttribute('data-fh-sg-panel') === id;
          panel.classList.toggle('is-active', on);
          panel.hidden = !on;
        });
      });
    });

    const urlEl = dialog.querySelector('[data-fh-kids-size-url]');
    if (!urlEl) return;
    let url = '';
    try {
      url = JSON.parse(urlEl.textContent);
    } catch (e) {
      url = urlEl.textContent.replace(/"/g, '').trim();
    }
    if (!url) return;

    const sizeOption = root.querySelector('[data-option-name="Size"], [data-option-name="size"]');
    const productSizes = sizeOption
      ? Array.from(sizeOption.querySelectorAll('[data-pb-pdp-value]')).map((b) => b.getAttribute('data-value'))
      : [];

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        const pairs = Array.isArray(data?.pairs) ? data.pairs : [];
        const matched = productSizes.length
          ? pairs.filter((p) => productSizes.includes(p.us))
          : pairs;
        if (!matched.length) return;
        const usBody = dialog.querySelector('[data-fh-sg-rows="us"]');
        const euBody = dialog.querySelector('[data-fh-sg-rows="eu"]');
        if (usBody) {
          usBody.innerHTML = matched
            .map((p) => `<tr><td>${p.us}</td><td>${p.eu || ''}</td></tr>`)
            .join('');
        }
        if (euBody) {
          euBody.innerHTML = matched
            .map((p) => `<tr><td>${p.eu || ''}</td><td>${p.us}</td></tr>`)
            .join('');
        }
      })
      .catch(() => {});
  }

  function initInfoEndLock(root) {
    const desktop = window.matchMedia('(min-width: 990px)');
    const grid = root.querySelector('.product-main-grid, .fh-pdp__stage');
    const inner = root.querySelector('.product-info-inner, .fh-pdp__info-inner');
    const sentinel = root.querySelector('[data-pdp-info-end], .product-info-end-sentinel');
    if (!grid || !inner || inner.dataset.endLockBound === '1') return;
    inner.dataset.endLockBound = '1';

    const BOTTOM_GAP = 24;

    const applyOffset = () => {
      const height = Math.ceil(inner.getBoundingClientRect().height);
      inner.style.setProperty('--pdp-info-height', `${height}px`);
      if (!desktop.matches || height < 1) {
        inner.style.setProperty('--pdp-end-lock-top', 'auto');
        inner.classList.remove('is-end-locked');
        return height;
      }
      const top = Math.round(window.innerHeight - height - BOTTOM_GAP);
      inner.style.setProperty('--pdp-end-lock-top', `${top}px`);
      inner.classList.toggle('is-end-locked', true);
      return height;
    };

    const resizeObserver = new ResizeObserver(applyOffset);
    resizeObserver.observe(inner);

    if (sentinel) {
      const io = new IntersectionObserver(
        () => {
          applyOffset();
          const box = inner.getBoundingClientRect();
          const atEnd = box.bottom <= window.innerHeight - BOTTOM_GAP + 8 && box.bottom > 0;
          inner.classList.toggle('is-end-locked', desktop.matches && (atEnd || box.top < 0));
        },
        {
          root: null,
          rootMargin: `0px 0px -${BOTTOM_GAP}px 0px`,
          threshold: [0, 1],
        }
      );
      io.observe(sentinel);
    }

    const onViewport = () => applyOffset();
    window.addEventListener('resize', onViewport, { passive: true });
    if (typeof desktop.addEventListener === 'function') desktop.addEventListener('change', onViewport);
    else desktop.addListener(onViewport);

    applyOffset();
    requestAnimationFrame(applyOffset);
  }

  function initStickyGallery(root) {
    const desktop = window.matchMedia('(min-width: 990px)');
    const stage = root.querySelector('.product-main-grid, .fh-pdp__stage');
    const gallery = root.querySelector('.product-media-column, .fh-pdp__gallery');
    if (!stage || !gallery || gallery.dataset.stickyGalleryBound === '1') return;
    gallery.dataset.stickyGalleryBound = '1';

    const TOP_GAP = 16;
    let naturalWidth = 0;
    let ticking = false;

    const clearPosition = () => {
      gallery.style.removeProperty('position');
      gallery.style.removeProperty('top');
      gallery.style.removeProperty('bottom');
      gallery.style.removeProperty('left');
      gallery.style.removeProperty('width');
    };

    const update = () => {
      ticking = false;
      if (!desktop.matches) {
        clearPosition();
        return;
      }

      if (!naturalWidth) {
        clearPosition();
        naturalWidth = gallery.getBoundingClientRect().width;
      }

      const stageBox = stage.getBoundingClientRect();
      const galleryHeight = gallery.offsetHeight;
      const endTop = stageBox.bottom - galleryHeight;

      if (stageBox.top >= TOP_GAP) {
        clearPosition();
      } else if (endTop <= TOP_GAP) {
        gallery.style.position = 'absolute';
        gallery.style.top = 'auto';
        gallery.style.bottom = '0';
        gallery.style.left = '0';
        gallery.style.width = `${naturalWidth}px`;
      } else {
        gallery.style.position = 'fixed';
        gallery.style.top = `${TOP_GAP}px`;
        gallery.style.bottom = 'auto';
        gallery.style.left = `${stageBox.left}px`;
        gallery.style.width = `${naturalWidth}px`;
      }
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', () => {
      naturalWidth = 0;
      clearPosition();
      requestUpdate();
    }, { passive: true });
    if (typeof desktop.addEventListener === 'function') desktop.addEventListener('change', requestUpdate);
    update();
  }

  function initFhPdp(scope) {
    const rootScope = scope && scope.querySelectorAll ? scope : document;
    const roots = rootScope.querySelectorAll('[data-fh-pdp], [data-pb-pdp]');

    roots.forEach((root) => {
      if (root.dataset.fhPdpBound === '1') return;
      root.dataset.fhPdpBound = '1';
      root.dataset.pbPdpBound = '1';

      const product = parseJson(root.querySelector('[data-pb-pdp-json]'));
      const thumbs = root.querySelectorAll('[data-pb-pdp-thumb]');
      const medias = Array.from(root.querySelectorAll('[data-pb-pdp-media]'));
      const dots = root.querySelectorAll('[data-pb-pdp-dot]');
      const slider = root.querySelector('[data-pb-pdp-slider]');
      const counter = root.querySelector('[data-fh-counter]');
      const lowStock = Number(root.getAttribute('data-low-stock') || product?.lowStock || 8);
      const isMobile = () => window.matchMedia('(max-width: 989px)').matches;
      let activeIndex = 0;

      const mark = (idx) => {
        activeIndex = idx;
        thumbs.forEach((t) => t.classList.toggle('is-active', Number(t.getAttribute('data-index')) === idx));
        dots.forEach((d) => d.classList.toggle('is-active', Number(d.getAttribute('data-index')) === idx));
        medias.forEach((m) => m.classList.toggle('is-active', Number(m.getAttribute('data-index')) === idx));
        if (counter && medias.length) counter.textContent = `${idx + 1} / ${medias.length}`;
      };

      const goToSlide = (idx, { smooth = true } = {}) => {
        const index = Math.max(0, Math.min(medias.length - 1, Number(idx)));
        const target = medias.find((m) => Number(m.getAttribute('data-index')) === index);
        mark(index);
        if (!target || !slider) return;
        const behavior = smooth && !reduced() ? 'smooth' : 'auto';
        if (isMobile()) {
          slider.scrollTo({ left: target.offsetLeft, behavior });
        }
        /* Desktop: CSS shows only .is-active — no scroll-through stack */
      };

      /* Desktop prev/next on main viewer */
      const prevBtn = root.querySelector('[data-fh-gallery-prev]');
      const nextBtn = root.querySelector('[data-fh-gallery-next]');
      if (prevBtn) {
        prevBtn.addEventListener('click', () => goToSlide(activeIndex - 1));
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => goToSlide(activeIndex + 1));
      }

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => goToSlide(thumb.getAttribute('data-index')));
      });
      dots.forEach((dot) => {
        dot.addEventListener('click', () => goToSlide(dot.getAttribute('data-index')));
      });

      if (slider && medias.length > 1) {
        let tick = 0;
        slider.addEventListener(
          'scroll',
          () => {
            if (!isMobile()) return;
            if (tick) return;
            tick = window.requestAnimationFrame(() => {
              tick = 0;
              const center = slider.scrollLeft + slider.clientWidth / 2;
              let closest = medias[0];
              let best = Infinity;
              medias.forEach((m) => {
                const mid = m.offsetLeft + m.clientWidth / 2;
                const dist = Math.abs(mid - center);
                if (dist < best) {
                  best = dist;
                  closest = m;
                }
              });
              mark(Number(closest.getAttribute('data-index')));
            });
          },
          { passive: true }
        );

        slider.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goToSlide(activeIndex + 1);
          }
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goToSlide(activeIndex - 1);
          }
        });
      }

      const zoomDlg = root.querySelector('[data-fh-zoom-dialog]');
      const zoomImg = root.querySelector('[data-fh-zoom-img]');
      root.querySelectorAll('[data-fh-zoom]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const img = btn.querySelector('img');
          if (!img || !zoomDlg || !zoomImg) return;
          zoomImg.src = img.currentSrc || img.src;
          zoomImg.alt = img.alt || '';
          if (typeof zoomDlg.showModal === 'function') zoomDlg.showModal();
        });
      });
      root.querySelector('[data-fh-zoom-close]')?.addEventListener('click', () => zoomDlg?.close());
      zoomDlg?.addEventListener('click', (event) => {
        if (event.target === zoomDlg) zoomDlg.close();
      });

      const qtyInput = root.querySelector('[data-pb-pdp-qty-input]');
      const minQty = Number(qtyInput?.min || 1);
      const bulkPanel = root.querySelector('[data-fh-bulk]');
      const bulkStatus = bulkPanel?.querySelector('[data-fh-bulk-status]');
      const bulkSynced = bulkPanel?.getAttribute('data-synced') === 'true';
      const bulkTiers = Array.from(root.querySelectorAll('[data-fh-pack]'))
        .map((btn) => ({
          el: btn,
          q: Number(btn.getAttribute('data-fh-pack') || 1),
          d: Number(btn.getAttribute('data-fh-discount') || 0),
        }))
        .sort((a, b) => a.q - b.q);

      const syncBulk = () => {
        if (!qtyInput || !bulkPanel) return;
        const qty = Number(qtyInput.value) || minQty;
        let current = bulkTiers[0];
        bulkTiers.forEach((tier) => {
          if (qty >= tier.q) current = tier;
        });
        bulkTiers.forEach((tier) => tier.el.classList.toggle('is-selected', tier === current));
        if (!bulkStatus) return;
        if (!bulkSynced) {
          bulkStatus.hidden = true;
          return;
        }
        const next = bulkTiers.find((tier) => tier.q > qty && tier.d > 0);
        const parts = [];
        if (current?.d > 0) parts.push(`You qualify for ${current.d}% off at checkout.`);
        if (next) parts.push(`Add ${next.q - qty} more to unlock ${next.d}% off.`);
        bulkStatus.hidden = parts.length === 0;
        bulkStatus.textContent = parts.join(' ');
      };

      root.querySelectorAll('[data-pb-pdp-qty]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (!qtyInput) return;
          const next = Math.max(minQty, (Number(qtyInput.value) || minQty) + Number(btn.getAttribute('data-pb-pdp-qty') || 0));
          qtyInput.value = String(next);
          syncBulk();
        });
      });

      bulkTiers.forEach((tier) => {
        tier.el.addEventListener('click', () => {
          if (qtyInput) qtyInput.value = String(Math.max(minQty, tier.q));
          syncBulk();
        });
      });
      qtyInput?.addEventListener('change', syncBulk);
      syncBulk();

      initTabs(root);
      initSizeGuide(root);
      initStickyGallery(root);

      if (!product || !Array.isArray(product.variants)) return;

      const optionNodes = Array.from(root.querySelectorAll('[data-pb-pdp-option]'));
      const selected = (product.options || []).map((optName, index) => {
        const node = optionNodes.find((n) => Number(n.getAttribute('data-option-index')) === index);
        const selectedBtn = node?.querySelector('[data-pb-pdp-value].is-selected');
        return selectedBtn?.getAttribute('data-value') || product.variants[0]?.options?.[index] || '';
      });

      const matchVariant = (sel) =>
        product.variants.find((v) => (v.options || []).every((opt, i) => String(opt) === String(sel[i])));

      const comboExists = (sel) => Boolean(matchVariant(sel));

      const resolveAfterChange = (index, value) => {
        const trial = selected.slice();
        trial[index] = value;
        if (comboExists(trial)) return trial;
        const orig = selected.slice();
        orig[index] = value;
        const candidate = product.variants.find((v) => String(v.options?.[index]) === String(value) && v.available)
          || product.variants.find((v) => String(v.options?.[index]) === String(value));
        if (candidate) return candidate.options.slice();
        return selected.slice();
      };

      const setHistory = (variant) => {
        if (!variant || !window.history?.replaceState) return;
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          window.history.replaceState({ variant: variant.id }, '', url);
        } catch (e) {}
      };

      const stockCopy = (variant) => {
        const labels = product.labels || {};
        const qty = Number(variant.inventory_quantity || 0);
        const preorder = product.preorder === true || !variant.available;
        if (preorder) return { state: 'preorder', label: labels.available_preorder || 'Available for preorder' };
        if (!variant.available || (variant.inventory_quantity === 0 && variant.inventory_policy !== 'continue')) {
          return { state: 'out', label: labels.sold_out || 'Sold out' };
        }
        if (qty > 0 && qty <= lowStock) return { state: 'low', label: (labels.only_left || 'Only {{ count }} left').replace('{{ count }}', qty) };
        return { state: 'in', label: labels.in_stock || 'In stock' };
      };

      const syncUI = () => {
        let variant = matchVariant(selected);
        if (!variant) {
          variant =
            product.variants.find((v) => v.available) ||
            product.variants[0];
          if (variant?.options) variant.options.forEach((opt, i) => { selected[i] = String(opt); });
        }
        if (!variant) return;

        const idInput = root.querySelector('[data-pb-pdp-variant-id]');
        if (idInput) idInput.value = variant.id;

        const priceEl = root.querySelector('[data-pb-pdp-price]');
        if (priceEl) {
          if (variant.compare_at_price > variant.price) {
            priceEl.innerHTML = `<span class="fh-pdp__price-now">${money(variant.price, product.money_format)}</span><s class="fh-pdp__price-was">${money(variant.compare_at_price, product.money_format)}</s>`;
          } else {
            priceEl.innerHTML = `<span class="fh-pdp__price-now fh-pdp__price-now--regular">${money(variant.price, product.money_format)}</span>`;
          }
        }

        const skuWrap = root.querySelector('[data-pb-pdp-sku]');
        if (skuWrap) {
          skuWrap.hidden = !variant.sku;
          const span = skuWrap.querySelector('span');
          if (span) span.textContent = variant.sku || '';
        }

        const atc = root.querySelector('[data-pb-pdp-atc]');
        const isPreorder = atc?.dataset.preorder === 'true' || product.preorder === true || !variant.available;
        if (atc) {
          const baseLabel = atc.dataset.label || 'Add To Cart';
          const canBuy = variant.available || isPreorder;
          atc.disabled = !canBuy;
          atc.classList.toggle('fh-pdp__atc--preorder', isPreorder);
          atc.textContent = canBuy ? (isPreorder ? (product.labels?.preorder || 'Pre-order') : baseLabel) : (product.labels?.sold_out || 'Sold out');
        }

        const preorderMessage = root.querySelector('[data-fh-preorder]');
        if (preorderMessage) preorderMessage.hidden = !isPreorder;

        const buyNow = root.querySelector('.fh-pdp__buy-now');
        if (buyNow) buyNow.hidden = isPreorder;

        const stock = root.querySelector('[data-pb-pdp-stock]');
        const stockLabel = root.querySelector('[data-pb-pdp-stock-label]');
        if (stock && stockLabel) {
          const copy = stockCopy(variant);
          stockLabel.textContent = copy.label;
          stock.dataset.state = copy.state;
          stock.classList.remove('is-in', 'is-low', 'is-out', 'is-preorder');
          stock.classList.add(`is-${copy.state}`);
        }

        if (variant.featured_media?.id != null) {
          const mediaEl = medias.find((m) => String(m.getAttribute('data-media-id')) === String(variant.featured_media.id));
          if (mediaEl) goToSlide(mediaEl.getAttribute('data-index'), { smooth: false });
        }

        optionNodes.forEach((node, index) => {
          const label = node.querySelector('[data-pb-pdp-option-value]');
          if (label) label.textContent = selected[index] || '';
          node.querySelectorAll('[data-pb-pdp-value]').forEach((btn) => {
            const value = btn.getAttribute('data-value') || '';
            const on = String(selected[index]) === String(value);
            btn.classList.toggle('is-selected', on);
            btn.setAttribute('aria-pressed', on ? 'true' : 'false');
            btn.setAttribute('aria-selected', on ? 'true' : 'false');

            const trial = selected.slice();
            trial[index] = value;
            const exact = matchVariant(trial);
            const exists = Boolean(exact) || product.variants.some((v) => String(v.options?.[index]) === String(value));
            const unavailable = !exists;
            btn.classList.toggle('is-unavailable', unavailable);
            if (!exists) btn.setAttribute('aria-disabled', 'true');
            else btn.removeAttribute('aria-disabled');
          });
        });

        setHistory(variant);
      };

      optionNodes.forEach((node) => {
        const index = Number(node.getAttribute('data-option-index'));
        node.querySelectorAll('[data-pb-pdp-value]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const value = btn.getAttribute('data-value') || '';
            const trial = selected.slice();
            trial[index] = value;
            if (!comboExists(trial) && btn.getAttribute('aria-disabled') === 'true') {
              const next = resolveAfterChange(index, value);
              next.forEach((opt, i) => { selected[i] = String(opt); });
            } else {
              selected[index] = value;
            }
            syncUI();
          });
        });
      });

      const form = root.querySelector('[data-pb-pdp-form]');
      if (form) {
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          const variant = matchVariant(selected);
          if (!variant) return;
          const isPreorder = form.getAttribute('data-pb-preorder') === 'true' || product.preorder === true || !variant.available;
          if (!variant.available && !isPreorder) return;
          const qty = Number(qtyInput?.value || 1);
          const payload = { id: variant.id, quantity: qty };
          if (isPreorder) payload.properties = { _Preorder: 'Yes', Preorder: 'Yes' };
          try {
            await window.pebbleAddToCart(payload);
          } catch (e) {
            console.error(e);
          }
        });
      }

      syncUI();
    });
  }

  window.initFhPdp = initFhPdp;

  function initReviewsBoard(scope) {
    const rootScope = scope && scope.querySelectorAll ? scope : document;
    rootScope.querySelectorAll('[data-fh-reviews]').forEach((root) => {
      if (root.dataset.fhReviewsBound === '1') return;
      const list = root.querySelector('[data-fh-review-list]');
      if (!list) return;
      root.dataset.fhReviewsBound = '1';
      const cards = Array.from(list.querySelectorAll('[data-fh-review-card]'));
      const page = Number(list.getAttribute('data-page-size') || 3);
      let shown = page;
      const more = root.querySelector('[data-fh-review-more]');
      const sort = root.querySelector('[data-fh-review-sort]');
      const paint = () => {
        cards.forEach((card, index) => {
          card.hidden = index >= shown;
        });
        if (more) more.hidden = shown >= cards.length;
      };
      sort?.addEventListener('change', () => {
        const mode = sort.value;
        cards.sort((a, b) => {
          if (mode === 'highest') return Number(b.dataset.rating) - Number(a.dataset.rating);
          if (mode === 'lowest') return Number(a.dataset.rating) - Number(b.dataset.rating);
          return Number(b.dataset.date) - Number(a.dataset.date);
        });
        cards.forEach((card) => list.appendChild(card));
        shown = page;
        paint();
      });
      more?.addEventListener('click', () => {
        shown += page;
        paint();
      });
      paint();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initFhPdp();
    initReviewsBoard();
  });
  document.addEventListener('shopify:section:load', (event) => {
    initFhPdp(event.target);
    initReviewsBoard(event.target);
  });
})();
